'use client';

import { useSnackbar } from 'notistack';
import { ComponentProps, FC } from 'react';

import { processPriceString } from 'src/general/helpers/processProductApiData';
import { FinanceEnquiryPayload, sendFinanceEnquiry } from 'src/services/LeadService';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { FinanceEnquiryFormValues } from '../../../hygiene/sections/FinanceCalculator/FinanceCalculator.type';
import { FinanceDescriptionContent } from '../../../hygiene/sections/flexible-content/FinanceDescriptionContent/FinanceDescriptionContent';
import { FlexibleSection } from '../../../hygiene/sections/flexible-content/flexibleContent.type';
import { FinancePdpCalculator } from '../../components/FinancePdpCalculator/FinancePdpCalculator';
import styles from './PdpFinanceSection.module.scss';

interface PdpFinanceSectionProps extends FlexibleSection {
  sectionData: ComponentProps<typeof FinanceDescriptionContent>['sectionData'] & {
    note: string;
    termOfLoan: {
      min: number;
      max: number;
    };
    interestRateRange: {
      min: number;
      max: number;
    };
    buttonLabel: string;
  };
  productDetails: ProductDetailsPayload;
  microcopy: ComponentProps<typeof FinancePdpCalculator>['microcopy'];
}

export const PdpFinanceSection: FC<PdpFinanceSectionProps> = ({ sectionData, productDetails, microcopy }) => {
  const errorMessage =
    "Sorry, we couldn't submit your message at this time. Please give us a call on 0283193210 or email us at hello@carma.com.au";
  const { enqueueSnackbar } = useSnackbar();

  const onSubmitEnquiry = async (data: FinanceEnquiryFormValues) => {
    const payload: FinanceEnquiryPayload = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.emailAddress,
      phone: data.phoneNumber,
      message: data.message,
      vehicle_id: productDetails.id,
      deposit_amount: data.depositAmount,
      interest_rate: data.interestRate,
      purchase_price: data.purchasePrice,
      term_of_loan: data.termOfLoan,
      weekly_payment: data.weeklyPayment,
    };
    const response = await sendFinanceEnquiry(payload);
    pushToDataLayer({
      event: 'pdp_finance_enquiry_form_submit',
      value: payload,
    });

    // handle error
    if (response?.status === 400) {
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }

    enqueueSnackbar('Your enquiry has been sent successfully', { variant: 'success' });
  };

  return (
    <section className={styles.root}>
      <div className={styles.contentWrapper}>
        <div className={styles.topWell}>
          <FinanceDescriptionContent sectionData={sectionData} inPDP />
        </div>
        <div className={styles.bottomWell}>
          <div className={styles.calWrapper}>
            <FinancePdpCalculator
              interestRateRange={{
                minRate: sectionData.interestRateRange.min,
                maxRate: sectionData.interestRateRange.max,
              }}
              loanTerms={sectionData.termOfLoan}
              onSubmit={onSubmitEnquiry}
              purchasePrice={processPriceString(productDetails.price)}
              microcopy={microcopy}
            />
          </div>
          <div className={styles.noteWrapper}>
            <p className={styles.note}>{sectionData.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
